const ICE = [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }];

export class HuddleClient {
  constructor({ sendSignal, onChange }) {
    this.sendSignal = sendSignal;
    this.onChange = onChange;
    this.peers = {};
    this.streams = {};
    this.localStream = null;
    this.huddleId = null;
    this.myId = null;
    this.muted = false;
    this.speaking = {};
    this.analysers = {};
    this.ctx = null;
    this.timer = null;
    this.micError = null;
  }

  async join(huddle, myId) {
    this.huddleId = huddle.id;
    this.myId = myId;
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
      this.watch(myId, this.localStream);
    } catch (e) {
      this.micError = e?.message || "Microphone unavailable";
      this.localStream = null;
    }
    huddle.participants.filter((p) => p !== myId).forEach((p) => this.connectTo(p, true));
    this.timer = setInterval(() => this.poll(), 200);
    this.emit();
  }

  createPeer(peerId) {
    const pc = new RTCPeerConnection({ iceServers: ICE });
    this.peers[peerId] = pc;
    this.localStream?.getTracks().forEach((t) => pc.addTrack(t, this.localStream));
    pc.onicecandidate = (e) => e.candidate && this.sendSignal(peerId, { kind: "ice", candidate: e.candidate });
    pc.ontrack = (e) => {
      this.streams[peerId] = e.streams[0];
      this.watch(peerId, e.streams[0]);
      this.emit();
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed"].includes(pc.connectionState)) this.drop(peerId);
      this.emit();
    };
    return pc;
  }

  async connectTo(peerId, initiator) {
    if (this.peers[peerId]) return;
    const pc = this.createPeer(peerId);
    if (initiator) {
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      this.sendSignal(peerId, { kind: "offer", sdp: pc.localDescription });
    }
  }

  async handleSignal(from, signal) {
    if (!this.huddleId) return;
    try {
      if (signal.kind === "offer") {
        const pc = this.peers[from] || this.createPeer(from);
        await pc.setRemoteDescription(signal.sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.sendSignal(from, { kind: "answer", sdp: pc.localDescription });
      } else if (signal.kind === "answer") {
        await this.peers[from]?.setRemoteDescription(signal.sdp);
      } else if (signal.kind === "ice") {
        await this.peers[from]?.addIceCandidate(signal.candidate);
      }
    } catch (e) {
      console.warn("huddle signal error", e);
    }
  }

  syncParticipants(ids) {
    Object.keys(this.peers).forEach((p) => !ids.includes(p) && this.drop(p));
    this.emit();
  }

  drop(peerId) {
    this.peers[peerId]?.close();
    delete this.peers[peerId];
    delete this.streams[peerId];
    delete this.analysers[peerId];
    delete this.speaking[peerId];
  }

  watch(id, stream) {
    try {
      this.ctx = this.ctx || new (window.AudioContext || window.webkitAudioContext)();
      const src = this.ctx.createMediaStreamSource(stream);
      const an = this.ctx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      this.analysers[id] = an;
    } catch { /* analyser unsupported */ }
  }

  poll() {
    let changed = false;
    const buf = new Uint8Array(256);
    Object.entries(this.analysers).forEach(([id, an]) => {
      an.getByteFrequencyData(buf);
      const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
      const sp = avg > 18 && !(id === this.myId && this.muted);
      if (sp !== !!this.speaking[id]) {
        this.speaking[id] = sp;
        changed = true;
      }
    });
    if (changed) this.emit();
  }

  toggleMute() {
    this.muted = !this.muted;
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = !this.muted));
    this.emit();
    return this.muted;
  }

  leave() {
    clearInterval(this.timer);
    Object.keys(this.peers).forEach((p) => this.drop(p));
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.huddleId = null;
    this.speaking = {};
    this.analysers = {};
    this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.emit();
  }

  emit() {
    this.onChange?.({ streams: { ...this.streams }, speaking: { ...this.speaking }, muted: this.muted, micError: this.micError, connected: Object.keys(this.peers).length });
  }
}
